import React, { useState, useEffect } from "react";
import Container from "./Container";
import OptionCal from "./OptionCal";
import ChartComponent from "./ChartComponent";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "./Button";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import PlTable from "../tables/PlTable";
import FdTable from "../tables/FdTable";
import RdTable from "../tables/RdTable";
import EarlyLoanTable from "../tables/EarlyLoanTable";
import EducationTable from "../tables/EducationTable";
import CreditPayTable from "../tables/CreditPayTable";
import BussinessLoanTable from "../tables/BussinessLoanTable";
import LtvTable from "../tables/LtvTable";
import SavingTable from "../tables/SavingTable";
import ContributionTable from "../tables/ContributionTable";
import LumpsumTable from "../tables/Lumpsumtable";
import useLocale from "../../src/locales/index";
import { setActiveCalculator } from "../redux/calculations/calculaterSlice";
import i18n from "../i18n";
import AIAnalysisModal from "./AIAnalysisModal";
import ExplainWithAI from "./ExplainWithAI";
import ExplainTerm from "./ExplainTerm";
import AppPromo from "./AppPromo";

const CalculatorLayout = ({
  title,
  description,
  children,
  activeTab,
  timeUnit,
  descriptionContent,
  showTable,
  setShowTable,
  setCalcDone,
  calcDone,
  isFormModified,
}) => {
  const { calculatorType } = useParams();

  const dispatch = useDispatch();
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { activeCalculator } = useSelector((state) => state.calculator);

  const { activeTab: aiActiveTab } = useSelector((state) => state.aiAnalysis);

  // const [calcDone, setCalcDone] = useState(false);
  // const [showTable, setShowTable] = useState(false);
  const [shouldGenerateReport, setShouldGenerateReport] = useState(false);
  const { textData } = useLocale();

  // Mapping between URL paths and calculator IDs
  const pathToCalculatorMap = {
    "sip-investment-calculator": "sipCalc",
    "personal-loan-calculator": "personalLoanCalc",
    "fixed-deposit-calculator": "FDCalc",
    "recurring-deposit-calculator": "RDCalc",
    "debt-to-income-ratio-calculator": "DTICalc",
    "net-worth-calculator": "netWorthCalc",
    "loan-payoff-calculator": "earlyLoanCalc",
    "student-education-loan-calculator": "educationLoanCalc",
    "credit-card-payoff-calculator": "creditCardPayoffCalc",
    "business-loan-calculator": "businessLoanCalc",
    "gst-tax-calculator": "GSTCalc",
    "salary-calculator": "salaryCalc",
    "pension-calculator": "pensionCalc",
    "car-lease-calculator": "carCalc",
    "mortgage-loan-refinance-calculator": "mortgageCalc",
    "401k-retirement-calculator": "contributionCalc",
    "saving-goal-calculator": "savingCalc",
    "loan-to-value-calculator": "loanToValueCalc",
  };

  // Get the calculator ID from the URL path
  const calculatorId = pathToCalculatorMap[calculatorType];

  const navigate = useNavigate();
  const [direction, setDirection] = useState("ltr");

  const calculators = [
    {
      name: textData.filterDropDown.sipCalc,
      path: "/calculator/sip-investment-calculator",
    },
    {
      name: textData.filterDropDown.personalLoanCalc,
      path: "/calculator/personal-loan-calculator",
    },
    {
      name: textData.filterDropDown.FDCalc,
      path: "/calculator/fixed-deposit-calculator",
    },
    {
      name: textData.filterDropDown.contributionCalc,
      path: "/calculator/401k-retirement-calculator",
    },
    {
      name: textData.filterDropDown.RDCalc,
      path: "/calculator/recurring-deposit-calculator",
    },
    {
      name: textData.filterDropDown.DTICalc,
      path: "/calculator/debt-to-income-ratio-calculator",
    },
    {
      name: textData.filterDropDown.GSTCalc,
      path: "/calculator/gst-tax-calculator",
    },
    {
      name: textData.filterDropDown.pensionCalc,
      path: "/calculator/pension-calculator",
    },
    { name: textData.showMore, path: "/" },
  ];

  useEffect(() => {
    const rtlLanguages = ["ar", "fa", "ur"];
    const currentLang = i18n.language;

    if (rtlLanguages.includes(currentLang)) {
      setDirection("rtl");
      document.documentElement.setAttribute("dir", "rtl");
    } else {
      setDirection("ltr");
      document.documentElement.setAttribute("dir", "ltr");
    }
  }, [i18n.language]);

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  useEffect(() => {
    if (calculatorId && calculatorId !== activeCalculator) {
      dispatch({ type: "SET_ACTIVE_CALCULATOR", payload: calculatorId });
    }
  }, [calculatorId, activeCalculator, dispatch]);

  const handleNavigate = () => {
    dispatch(setActiveCalculator(""));
    navigate("/");
  };
  const calculatorTitleMap = {
    sipCalc: "filterDropDown.sipCalc",
    personalLoanCalc: "filterDropDown.personalLoanCalc",
    FDCalc: "filterDropDown.FDCalc",
    contributionCalc: "filterDropDown.contributionCalc",
    RDCalc: "filterDropDown.RDCalc",
    DTICalc: "filterDropDown.DTICalc",
    educationLoanCalc: "filterDropDown.educationLoanCalc",
    creditCardPayoffCalc: "filterDropDown.creditCardPayoffCalc",
    businessLoanCalc: "filterDropDown.businessLoanCalc",
    earlyLoanCalc: "filterDropDown.earlyLoanCalc",
    netWorthCalc: "filterDropDown.netWorthCalc",
    GSTCalc: "filterDropDown.GSTCalc",
    VatCalc: "filterDropDown.VatCalc",
    salaryCalc: "filterDropDown.salaryCalc",
    pensionCalc: "filterDropDown.pensionCalc",
    loanToValueCalc: "filterDropDown.loanToValueCalc",
    savingCalc: "filterDropDown.savingCalc",
    carCalc: "filterDropDown.carCalc",
    mortgageCalc: "filterDropDown.mortgageCalc",
  };
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((o, p) => o?.[p], obj);
  };

  const getCalculatorTitle = () => {
    const titleKey = calculatorTitleMap[calculatorId];
    if (titleKey && textData) {
      return getNestedValue(textData, titleKey);
    }
    return "Calculator";
  };

  const handleCalculationDone = () => {
    setCalcDone(true);
    setShowTable(false);
    setShouldGenerateReport(false);
  };
  const handleClick = (path) => {
    dispatch(setActiveCalculator(""));
    navigate(path);
  };

  React.useEffect(() => {
    if (
      React.Children.count(children) === 1 &&
      React.isValidElement(children)
    ) {
      const childWithProps = React.cloneElement(children, {
        onCalculationDone: handleCalculationDone,
      });
      children = childWithProps;
    }
  }, [children, showTable, setShowTable, setShouldGenerateReport]);

  return (
    <div
      className={`flex gap-3 flex-col ${direction === "rtl" ? "rtl" : "ltr"
        }`}
    >
      <div className="flex container gap-3 flex-col">
        <div className="flex flex-col lg110:flex-row gap-3 justify-center">
          <div className=" !pl-0 sm:px-10 md:px-[80px] lg:px-[10px] xl:px-[10px] mt-5 sm:mt-6 md:mt-6 text-start">
            <div className="pb-4">
              <span
                className="text-base sm:text-base font-BlinkMacSystemFont font-medium text-blue-shade cursor-pointer"
                onClick={handleNavigate}
              >
                {textData.calculator}
              </span>
              <span className="text-gray-font text-base font-BlinkMacSystemFont font-medium">
                {" "}
                &gt; {getCalculatorTitle()}
              </span>
            </div>
            <OptionCal />
            <div className="border border-solid border-gray-light rounded-xl px-[31px] py-[16px] lg110:max-w-[705px] xl:w-[942px] xl:max-w-[942px] mx-0 sm:mx-auto">
              <div className="flex flex-col gap-[42px]">
                <Container>
                  <div>
                    <div className="flex items-center justify-center gap-2">
                      <h1 className="lg:text-xl sm:text-lg text-[20px] md:max-w-full max-w-[260px]   text-black  font-lato-bold-700 text-center">
                        {textData.calculate}{" "}
                        <span className="bg-gradient-primary bg-clip-text text-[transparent]">
                          {title}{" "}
                        </span>
                      </h1>
                      <ExplainTerm term={calculatorType === "gst-tax-calculator" ? aiActiveTab === "GSTCalc" ? "GST Calculator" : "VAT Calculator" : getCalculatorTitle()} calculatorName={calculatorType === "gst-tax-calculator" ? aiActiveTab === "GSTCalc" ? "gstCalculator" : "vatCalculator" : calculatorType} />
                    </div>
                    <div className="sm:pt-2 pb-4 sm:max-w-[984px] max-w-[335px] w-full m-auto text-[14px] text-gray-font font-lato-regular-400 pt-2 text-center">
                      {description ||
                        "Explore the Potential Growth of Your Investments with our Calculator. Gain insights into returns and determine optimal investment amounts tailored to your financial objectives."}
                    </div>
                  </div>
                </Container>
                <div className="bg-white mb-[20px] rounded-[24px] w-full lg:flex block px-0 py-0 gap-[42px]">
                  <div className="lg:max-w-[508px] w-full flex flex-col pb-6 lg:pb-0">
                    {children}
                  </div>
                  <div className="w-full lg:w-[330px] lg:flex-shrink-0 bg-[#F6F8FB] rounded-[20px] md:mt-0 h-fit sm:p-[24px] p-[10px]">
                    <ChartComponent
                      activeTab={activeTab}
                      setShowTable={setShowTable}
                      calcDone={calcDone}
                      activeCalculator={activeCalculator}
                    // calculatorType={activeCalculator}
                    />
                    {
                      <div className="sm:mt-[25px] mt-[18px]">
                        <ExplainWithAI isDisabled={!calcDone} analysisType={calculatorType} calculatorName={calculatorType} className="mb-3" />
                        {calculatorId !== "salaryCalc" &&
                          calculatorId !== "netWorthCalc" &&
                          calculatorId !== "carCalc" &&
                          calculatorId !== "DTICalc" &&
                          calculatorId !== "GSTCalc" &&
                          calculatorId !== "VatCalc" &&
                          calculatorId !== "pensionCalc" &&
                          calculatorId !== "mortgageCalc" && <Button
                            className={`w-full ${isFormModified
                              ? "pointer-events-none opacity-50"
                              : ""
                              }`}
                            disabled={isFormModified}
                            onClick={async () => {
                              if (cookieAccept && analytics) {
                                logEvent(analytics, "report_details");
                              }
                              setShowTable(true);
                              setShouldGenerateReport(true);
                              setTimeout(() => {
                                const faqsForm =
                                  document.getElementById("tableScroll");
                                if (faqsForm) {
                                  const scrollMargin = 200;
                                  const scrollPosition =
                                    faqsForm.getBoundingClientRect().top +
                                    window.pageYOffset -
                                    scrollMargin;
                                  window.scrollTo({
                                    top: scrollPosition,
                                    behavior: "smooth",
                                  });
                                }
                              }, 100);
                            }}
                          >
                            {textData.details}
                          </Button>}
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* calc. option */}
          <div className=" lg:pl-[10px] sm:px-10 md:px-[80px] lg:px-[10px] xl:px-[10px] mx-auto text-start">
            <div className="w-full lg:w-[303px] h-fit border border-gray-1 bg-white rounded-lg shadow-lg overflow-hidden lg110:mt-[63px] mx-auto">
              <h2 className="bg-gray-100 px-5 py-4 border-b border-gray-1 font-semibold text-black-1 text-sm">
                {textData.popularCalculator}
              </h2>
              {calculators.map((calc, index) => (
                <button
                  key={index}
                  onClick={() => handleClick(calc.path)}
                  className={`w-full  px-5 py-[14px] ${isRTL() ? "text-right" : "text-left"
                    } text-sm text-black-1 hover:bg-gray-100 active: border-b border-gray-1 last:border-b-0`}
                >
                  {calc.name}
                </button>
              ))}
            </div>
            <div className="w-full lg:w-[303px] mx-auto">
              <AppPromo className="mt-6 w-full" />
            </div>
          </div>
        </div>
        <div className="lg110:max-w-[1040px] xl:max-w-[1280px] lg:w-[1280px]">
          {
            showTable && <div className="mt-5">
              <div className="lg:p-[20px] xl:p-[30px] p-[18px] border border-solid border-gray-light rounded-xl !pt-0">
                {calculatorId === "personalLoanCalc" && showTable && (
                  <PlTable
                    showTable={showTable}
                    activeTab={activeTab}
                    calcDone={calcDone}
                    historyData={null}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                  />
                )}

                {calculatorId === "FDCalc" && (
                  <FdTable
                    showTable={showTable}
                    timeUnit={timeUnit}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "RDCalc" && (
                  <RdTable
                    showTable={showTable}
                    timeUnit={timeUnit}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "earlyLoanCalc" && (
                  <EarlyLoanTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "educationLoanCalc" && (
                  <EducationTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}

                {calculatorId === "creditCardPayoffCalc" && (
                  <CreditPayTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "businessLoanCalc" && (
                  <BussinessLoanTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "lumpsumCalc" && (
                  <LumpsumTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}

                {calculatorId === "loanToValueCalc" && (
                  <LtvTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "savingCalc" && (
                  <SavingTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
                {calculatorId === "contributionCalc" && (
                  <ContributionTable
                    showTable={showTable}
                    shouldGenerateReport={shouldGenerateReport}
                    setShouldGenerateReport={setShouldGenerateReport}
                    calcDone={calcDone}
                  />
                )}
              </div>
            </div>
          }
          {
            descriptionContent && (
              <div className="mt-5">
                <div className="border border-solid border-gray-light rounded-xl">
                  {descriptionContent}
                </div>
              </div>
            )
          }
        </div>
      </div>

      <AIAnalysisModal />
    </div >
  );
};

export default CalculatorLayout;
